import { ChevronLeftRounded, ChevronRightRounded } from '@mui/icons-material'
import {
  FormControlLabel,
  Radio,
  RadioGroup,
  CircularProgress,
  Typography,
} from '@mui/material'
import { useIsAuth } from 'feature/auth/hooks'
import { useGetBankDetailsListQuery } from 'feature/company/redux/company.api'
import { orderFormSpace } from 'feature/productOrder/namespaces/orderFormSpace'
import { useEffect } from 'react'
import { useActions, useAppSelector } from 'store/hooks'

import { OrderStepLayout } from '../CreateOrder/OrderStepLayout'
import { ProductOrderFooter } from '../CreateOrder/ProductOrderFooter'
import { ProductOrderHeader } from '../CreateOrder/ProductOrderHeader'
import { StepComponentProps } from '../types/StepComponentProps'

import style from './OrderStepBankDetails.module.scss'

// шаг 7 (только для ЮЛ)
// выбор банковских реквизитов
export function OrderStepBankDetails({
  completeStep,
  handlePrevious,
}: StepComponentProps) {
  const isAuth = useIsAuth()

  const { setBankDetailsIdOrder } = useActions()
  // Этот шаг рендерится только для ЮЛ, поэтому читаем напрямую из newCompany
  // (не через currentEditForm, который может быть устаревшим на первый рендер)
  const bankDetailsId = useAppSelector(
    state =>
      state.orderForm.forms[orderFormSpace.FormType.newCompany].bankDetailsId,
  )

  const { data: bankDetailsList, isLoading } = useGetBankDetailsListQuery(
    undefined,
    { skip: !isAuth },
  )

  // При загрузке списка: если сохранённый id не найден — сбрасываем и выбираем последний
  useEffect(() => {
    if (!bankDetailsList?.items?.length) return
    const items = bankDetailsList.items
    const isValid =
      bankDetailsId !== null && items.some(i => i.id === bankDetailsId)
    if (!isValid) {
      setBankDetailsIdOrder(items[items.length - 1].id)
    }
  }, [bankDetailsList])

  const handlePreviousStep = () => handlePrevious(false)
  const handleCompleteStep = () => completeStep(false)

  const handleSelect = (id: number) => {
    setBankDetailsIdOrder(id)
  }

  return (
    <OrderStepLayout>
      <ProductOrderHeader step='Шаг 4' title='Банковские реквизиты' />

      <div className={style.content}>
        {isLoading && <CircularProgress />}
        {!isLoading && !bankDetailsList?.items?.length && (
          <p className={style.emptyText}>
            У вас нет сохранённых банковских реквизитов.{' '}
            <a href='/settings/bank-details' target='_blank' rel='noreferrer'>
              Добавить реквизиты
            </a>
          </p>
        )}
        {!isLoading && !!bankDetailsList?.items?.length && (
          <RadioGroup
            value={
              bankDetailsId !== null && bankDetailsId !== undefined
                ? String(bankDetailsId)
                : ''
            }
            onChange={e => handleSelect(Number(e.target.value))}
          >
            {bankDetailsList.items.map(item => (
              <FormControlLabel
                key={item.id}
                value={String(item.id)}
                control={
                  <Radio data-testid={`bank-details-radio-${item.id}`} />
                }
                label={
                  <div className={style.bankDetailsLabel}>
                    <Typography className={style.bankDetailsLabel__name}>
                      {item.bankName}
                    </Typography>
                    <Typography className={style.bankDetailsLabel__bik}>
                      БИК: {item.bik}
                    </Typography>
                    <Typography className={style.bankDetailsLabel__account}>
                      К/С: {item.correspondentAccount}
                    </Typography>
                    <Typography className={style.bankDetailsLabel__account}>
                      Р/С: {item.currentAccount}
                    </Typography>
                  </div>
                }
              />
            ))}
          </RadioGroup>
        )}
      </div>
      <ProductOrderFooter
        nextButton={{
          onClick: handleCompleteStep,
          Icon: <ChevronRightRounded />,
          text: 'Следующий шаг',
          disabled:
            isLoading ||
            !bankDetailsList ||
            (!bankDetailsId && !!bankDetailsList.items?.length),
        }}
        prevButton={{
          onClick: handlePreviousStep,
          Icon: <ChevronLeftRounded />,
          text: 'Предыдущий шаг',
        }}
      />
    </OrderStepLayout>
  )
}
